import { Typography, Box } from "@mui/material";
import ProjectDescription from "components/AppLayout/ProjectDescription";
import { motion } from "framer-motion";
import { useState } from "react";

const projects = [
  {
    projectTitle: "REVIVN",
    role: "Software Engineer",
    year: "2021–Present",
    company: "REVIVN",
    projectDescription:
      "Early engineer building backend systems and full-stack features for a growing circular-economy startup.",
    bullets: [
      "Designed scalable background job pipelines with prioritized queues and rate limiting, reducing API recovery time by 23%.",
      "Led end-to-end design and delivery of a new product initiative, generating a revenue stream that now accounts for 9% of total company revenue.",
      "Built reusable API client frameworks with standardized authentication, structured error handling, and consistent retry strategies across third-party integrations.",
    ],
    techStack: ["Rails", "React", "TypeScript", "PostgreSQL", "AWS", "Sidekiq"],
    focus: [
      "Reliability",
      "Integrations",
      "Background Processing",
      "System Design",
    ],
  },
  {
    projectTitle: "RIDEONTIME",
    role: "Software Engineer",
    year: "2021",
    company: "RIDEONTIME",
    projectDescription:
      "Software Engineer for a transit captive portal application serving thousands of daily riders.",
    bullets: [
      "Led product discovery and onboarding flow design, improving first-time connection success by 33%.",
      "Built responsive React frontend deployed to production environments.",
      "Integrated real-time MTA APIs while handling latency and reliability constraints to maintain 97% uptime.",
    ],
    techStack: ["React", "Node", "External APIs, Tailwind CSS"],
    focus: ["Frontend Architecture", "API Integration", "UX Systems"],
  },
  {
    projectTitle: "SWYFT",
    role: "Product Designer",
    year: "2018–2021",
    company: "SWYFT",
    projectDescription:
      "Bridged product and engineering during major organizational scaling.",
    bullets: [
      "Consolidated multiple related products into one unified system, reducing development costs by 25%.",
      "Led cross-department process re-engineering across 7 teams, cutting reporting time by 75%.",
      "Partnered closely with engineering leadership to define scalable system structures during rapid organizational growth.",
    ],
    techStack: [],
    focus: ["Systems Thinking", "Product Strategy", "Cross-Team Leadership"],
  },
];

export default function WorkPage() {
  const [selectedProject, setSelectedProject] = useState(0);

  const handleProjectClick = (index: number) => {
    setSelectedProject(index);
  };

  const sectionTitleSx = {
    fontSize: { xs: "5.25rem", md: "100px" },
  };

  return (
    <section id="Work">
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 2fr" }}
        gap={10}
        height={{ xs: "auto", md: "100vh" }}
        minHeight={{ xs: "100vh", md: "100vh" }}
      >
        {/* Desktop: left column = title + description. Mobile: stacked with project switcher first */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 0 },
          }}
        >
          <Typography variant="h2" fontFamily="Canopee" sx={sectionTitleSx}>
            Work
          </Typography>

          {/* Mobile only: horizontal project switcher (compact tabs) */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
              alignItems: "center",
            }}
          >
            {projects.map((project, index) => (
              <motion.div
                key={index}
                whileTap={{ scale: 0.96 }}
                style={{ cursor: "pointer" }}
                onClick={() => handleProjectClick(index)}
              >
                <Typography
                  fontFamily="Canopee"
                  sx={{
                    fontSize: "1.25rem",
                    textDecoration:
                      selectedProject === index ? "underline" : "none",
                    opacity: selectedProject === index ? 1 : 0.6,
                  }}
                >
                  {project.projectTitle}
                </Typography>
              </motion.div>
            ))}
          </Box>

          <ProjectDescription
            projectTitle={projects[selectedProject].projectTitle}
            projectDescription={projects[selectedProject].projectDescription}
            bullets={projects[selectedProject].bullets}
            techStack={projects[selectedProject].techStack}
            year={projects[selectedProject].year}
            role={projects[selectedProject].role}
            company={projects[selectedProject].company}
            focus={projects[selectedProject].focus}
          />
        </Box>

        {/* Desktop only: right column with project titles */}
        <Box
          sx={{
            mt: { md: 15 },
            display: { xs: "none", md: "block" },
          }}
        >
          {projects.map((project, index) => (
            <div
              key={index}
              style={{ cursor: "pointer" }}
              onClick={() => handleProjectClick(index)}
            >
              <motion.div
                whileHover={{ scale: 0.95 }}
                whileTap={{ scale: 0.9 }}
              >
                <Typography variant="h2" fontFamily="Canopee">
                  {project.projectTitle}
                </Typography>
              </motion.div>
            </div>
          ))}
        </Box>
      </Box>
    </section>
  );
}
